import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { categoryLabel, formatPrice } from "../components/ProductCard";

export default function ProductDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    setProduct(null); setNotFound(false);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-6 md:px-12 text-[#A3A39D]">{t("product.not_found")}</div>;
  if (!product) return <div className="pt-40 pb-24 max-w-[1500px] mx-auto px-6 md:px-12 text-[#A3A39D]">{t("catalog.loading")}</div>;

  const handleAdd = () => { add(product, qty); setAdded(true); setTimeout(() => setAdded(false), 1800); };
  const handleBuyNow = () => { add(product, qty); navigate("/commande"); };

  return (
    <div data-testid="product-detail-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-20">
        <div className="bg-[#151516] aspect-[4/5]">
          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
        </div>

        <div className="flex flex-col justify-center max-w-xl">
          <div className="overline mb-5">{categoryLabel(product.category, t)}</div>
          <h1 className="font-serif text-4xl md:text-6xl text-[#F5F5F0] leading-[1] mb-6">{product.name}</h1>
          <div className="text-2xl md:text-3xl text-[#D4AF37] font-serif mb-10">{formatPrice(product.price)}</div>
          <div className="hairline mb-8"></div>
          <p className="text-[#A3A39D] leading-relaxed mb-10">{product.description}</p>

          <dl className="grid grid-cols-2 gap-6 mb-10 text-sm">
            {product.origin && (
              <div>
                <dt className="overline mb-1 text-[10px]">{t("product.origin")}</dt>
                <dd className="text-[#F5F5F0]">{product.origin}</dd>
              </div>
            )}
            {product.material && (
              <div>
                <dt className="overline mb-1 text-[10px]">{t("product.material")}</dt>
                <dd className="text-[#F5F5F0]">{product.material}</dd>
              </div>
            )}
            <div>
              <dt className="overline mb-1 text-[10px]">{t("product.availability")}</dt>
              <dd className="text-[#F5F5F0]">
                {product.stock > 0 ? t("product.pieces", { count: product.stock }) : t("product.sold_out")}
              </dd>
            </div>
          </dl>

          <div className="flex items-center gap-5 mb-6">
            <div className="flex items-center border border-white/10">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:text-[#D4AF37]" aria-label={t("product.decrease")}>
                <Minus size={14} strokeWidth={1.25} />
              </button>
              <span data-testid="qty-value" className="px-5 text-sm tracking-widest">{qty}</span>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="p-3 hover:text-[#D4AF37]" aria-label={t("product.increase")}>
                <Plus size={14} strokeWidth={1.25} />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button onClick={handleAdd} data-testid="add-to-cart-btn" className="btn-primary inline-flex items-center gap-2">
              <ShoppingBag size={16} strokeWidth={1.25} />
              {added ? t("product.added") : t("product.add_to_cart")}
            </button>
            <button onClick={handleBuyNow} data-testid="buy-now-btn" className="btn-ghost">{t("product.buy_now")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
